import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/common/Button';
import { Modal } from '@/components/common/Modal';
import { SelectField, TextField } from '@/components/forms/Field';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { invoicesApi, paymentsApi } from '@/services/api/endpoints/finance';
import { queryKeys } from '@/services/api/queryKeys';
import type { UUID } from '@/types/domain';
import type { PaymentMethod } from '@/types/finance';
import { formatDate, formatMoney, orDash } from '@/utils/format';
import { StudentPicker } from './StudentPicker';
import { Money } from './shared';

const METHODS: { value: PaymentMethod; label: string }[] = [
  { value: 'CASH', label: 'Cash' },
  { value: 'BANK_TRANSFER', label: 'Bank transfer' },
  { value: 'CARD', label: 'Card' },
  { value: 'ONLINE', label: 'Online' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'MOBILE_WALLET', label: 'Mobile wallet' },
  { value: 'OTHER', label: 'Other' },
];

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Taking money.
 *
 * <p>The child is chosen first and everything else follows from it: their
 * balance, their unpaid bills, and how much of this payment lands where. Asking
 * for an amount before knowing who is paying is how money ends up on the wrong
 * account.
 *
 * <p>Allocation is offered two ways. Oldest-first is the default because it is
 * what a payment "off the account" means, and it stops one bill ageing while
 * newer ones are settled. Choosing the invoices by hand is there for the case
 * the default cannot know about — a parent paying specifically for the trip.
 *
 * <p>Nothing here decides a final figure. The backend caps every allocation at
 * the invoice's own outstanding balance and returns what it actually did, which
 * is what the receipt is then built from.
 */
export function RecordPaymentDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const [student, setStudent] = useState<{ id: UUID; name: string; number?: string } | null>(null);
  const [form, setForm] = useState({
    amount: '',
    paymentMethod: 'CASH' as PaymentMethod,
    paymentDate: today(),
    referenceNumber: '',
    bankName: '',
    notes: '',
    pending: false,
  });
  const [mode, setMode] = useState<'auto' | 'manual' | 'hold'>('auto');
  const [manual, setManual] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setStudent(null);
      setForm({
        amount: '', paymentMethod: 'CASH', paymentDate: today(),
        referenceNumber: '', bankName: '', notes: '', pending: false,
      });
      setMode('auto');
      setManual({});
    }
  }, [open]);

  const balance = useQuery({
    queryKey: queryKeys.payments.balance(student?.id ?? 'none'),
    queryFn: () => paymentsApi.balance(student?.id as UUID),
    enabled: open && student !== null,
  });

  const outstanding = useQuery({
    queryKey: queryKeys.invoices.list({ owing: true, student: student?.id }),
    queryFn: () =>
      invoicesApi.list({ page: 0, size: 50, studentId: student?.id, outstandingOnly: true }),
    enabled: open && student !== null,
  });

  const unpaid = useMemo(
    () =>
      (outstanding.data?.data ?? [])
        .filter((invoice) => invoice.acceptsPayment)
        .sort((a, b) => a.dueDate.localeCompare(b.dueDate)),
    [outstanding.data],
  );

  const manualTotal = Object.values(manual).reduce(
    (sum, value) => sum + (Number(value) || 0),
    0,
  );

  const record = useMutation({
    mutationFn: () =>
      paymentsApi.record({
        studentId: student?.id as UUID,
        amount: Number(form.amount),
        paymentMethod: form.paymentMethod,
        paymentDate: form.paymentDate,
        ...(form.referenceNumber !== '' ? { referenceNumber: form.referenceNumber } : {}),
        ...(form.bankName !== '' ? { bankName: form.bankName } : {}),
        ...(form.notes !== '' ? { notes: form.notes } : {}),
        pending: form.pending,
        autoAllocate: mode === 'auto',
        ...(mode === 'manual'
          ? {
              allocations: Object.entries(manual)
                .filter(([, amount]) => Number(amount) > 0)
                .map(([invoiceId, amount]) => ({ invoiceId, amount: Number(amount) })),
            }
          : {}),
      }),
    onSuccess: (result) => {
      const settled = result.settledInvoices.length;
      toast.success(
        `${result.payment.receiptNumber} recorded` +
          (settled > 0 ? ` — ${settled} invoice${settled === 1 ? '' : 's'} settled` : ''),
      );
      for (const warning of result.warnings) {
        toast.info(warning);
      }
      for (const discount of result.earlyPaymentDiscounts) {
        toast.success(`Early-payment discount applied: ${discount.name}`);
      }
      void queryClient.invalidateQueries({ queryKey: queryKeys.payments.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all });
      onClose();
    },
    onError: (error) => handleApiError(error),
  });

  const currency = balance.data?.currencyCode ?? 'PKR';
  const amount = Number(form.amount) || 0;
  const canSubmit =
    student !== null &&
    amount > 0 &&
    form.paymentDate !== '' &&
    (mode !== 'manual' || manualTotal > 0);

  return (
    <Modal
      open={open}
      size="lg"
      title="Record a payment"
      description="Money is recorded against the child; how it settles their bills is the next decision."
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button loading={record.isPending} disabled={!canSubmit} onClick={() => record.mutate()}>
            {amount > 0 ? `Take ${formatMoney(amount, currency)}` : 'Record payment'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <StudentPicker value={student} onChange={setStudent} />

        {student !== null && balance.data !== undefined && (
          <div className="grid grid-cols-3 gap-px border border-line bg-line">
            <Figure label="Outstanding" value={formatMoney(balance.data.totalOutstanding, currency)} />
            <Figure label="Overdue" value={formatMoney(balance.data.overdueAmount, currency)}
                    tone={balance.data.overdueAmount > 0 ? 'danger' : undefined} />
            <Figure label="Held on account" value={formatMoney(balance.data.creditHeld, currency)} />
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField label="Amount" type="number" step="0.01" min="0.01" required
                     value={form.amount}
                     onChange={(event) => setForm((f) => ({ ...f, amount: event.target.value }))} />
          <TextField label="Date received" type="date" required
                     value={form.paymentDate}
                     onChange={(event) => setForm((f) => ({ ...f, paymentDate: event.target.value }))} />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField label="Method" required value={form.paymentMethod}
                       onChange={(event) =>
                         setForm((f) => ({ ...f, paymentMethod: event.target.value as PaymentMethod }))}>
            {METHODS.map((method) => (
              <option key={method.value} value={method.value}>{method.label}</option>
            ))}
          </SelectField>
          <TextField label="Reference"
                     placeholder="Cheque number, bank reference, transaction id"
                     value={form.referenceNumber}
                     onChange={(event) => setForm((f) => ({ ...f, referenceNumber: event.target.value }))} />
        </div>

        {form.paymentMethod === 'CHEQUE' && (
          <label className="flex items-start gap-2.5 border border-line bg-surface-2 px-3.5 py-3">
            <input type="checkbox" className="mt-0.5 h-4 w-4 border-line" checked={form.pending}
                   onChange={(event) => setForm((f) => ({ ...f, pending: event.target.checked }))} />
            <span>
              <span className="block text-[13px] font-medium text-ink">Not yet cleared</span>
              <span className="block text-xs text-muted">
                An uncleared cheque is a promise of money, not money. It is recorded but settles
                no bill until you mark it cleared.
              </span>
            </span>
          </label>
        )}

        {student !== null && !form.pending && (
          <fieldset>
            <legend className="mb-1.5 text-xs font-medium text-muted">What this settles</legend>
            <div className="space-y-1.5">
              <Choice checked={mode === 'auto'} onChange={() => setMode('auto')}
                      title="Oldest bills first"
                      detail="Settles unpaid invoices in due-date order. Anything left over is held on account." />
              <Choice checked={mode === 'manual'} onChange={() => setMode('manual')}
                      title="Choose the invoices"
                      detail="For a payment made against particular bills."
                      disabled={unpaid.length === 0} />
              <Choice checked={mode === 'hold'} onChange={() => setMode('hold')}
                      title="Hold it on account"
                      detail="Record the money without applying it to anything yet." />
            </div>

            {mode === 'manual' && (
              unpaid.length === 0 ? (
                <p className="mt-2 text-[13px] text-muted">
                  This student has no bills that can take a payment.
                </p>
              ) : (
                <div className="mt-2 border border-line">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-line bg-surface-2">
                        <th scope="col" className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-subtle">
                          Invoice
                        </th>
                        <th scope="col" className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-subtle">
                          Owes
                        </th>
                        <th scope="col" className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-subtle">
                          Apply
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line">
                      {unpaid.map((invoice) => (
                        <tr key={invoice.id}>
                          <td className="px-3 py-2">
                            <span className="font-mono text-xs text-ink">{invoice.invoiceNumber}</span>
                            <span className="block text-[11px] text-subtle">
                              {orDash(invoice.billingPeriod)} · due {formatDate(invoice.dueDate)}
                            </span>
                          </td>
                          <td className="px-3 py-2"><Money amount={invoice.outstandingAmount} /></td>
                          <td className="px-3 py-2">
                            <input
                              type="number" step="0.01" min="0" max={invoice.outstandingAmount}
                              value={manual[invoice.id] ?? ''}
                              onChange={(event) =>
                                setManual((current) => ({ ...current, [invoice.id]: event.target.value }))}
                              placeholder="0.00"
                              aria-label={`Amount to apply to ${invoice.invoiceNumber}`}
                              className="w-28 border border-line bg-surface px-2 py-1 text-right text-sm tabular-nums text-ink focus:border-accent focus:outline-none"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="flex items-baseline justify-between border-t-2 border-line px-3 py-2">
                    <span className="text-[13px] text-muted">Allocated</span>
                    <span className="text-[13px] tabular-nums text-ink">
                      {formatMoney(manualTotal, currency)}
                      {amount > 0 && manualTotal < amount && (
                        <span className="ml-2 text-subtle">
                          · {formatMoney(amount - manualTotal, currency)} held
                        </span>
                      )}
                    </span>
                  </div>
                  {manualTotal > amount && amount > 0 && (
                    <p className="border-t border-line px-3 py-2 text-xs text-warning">
                      More is allocated than is being paid. The backend will apply only what
                      arrived, in the order listed.
                    </p>
                  )}
                </div>
              )
            )}
          </fieldset>
        )}

        <TextField label="Notes" placeholder="Anything worth recording about this payment"
                   value={form.notes}
                   onChange={(event) => setForm((f) => ({ ...f, notes: event.target.value }))} />
      </div>
    </Modal>
  );
}

function Figure({ label, value, tone }: { label: string; value: string; tone?: 'danger' }) {
  return (
    <div className="bg-surface px-3 py-2.5">
      <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-subtle">
        {label}
      </span>
      <span className={`mt-0.5 block text-[15px] tabular-nums ${tone === 'danger' ? 'text-danger' : 'text-ink'}`}>
        {value}
      </span>
    </div>
  );
}

function Choice({ checked, onChange, title, detail, disabled = false }: {
  checked: boolean;
  onChange: () => void;
  title: string;
  detail: string;
  disabled?: boolean;
}) {
  return (
    <label className={`flex items-start gap-2.5 border px-3.5 py-2.5 ${
      checked ? 'border-accent bg-accent-soft' : 'border-line bg-surface'
    } ${disabled ? 'opacity-50' : 'cursor-pointer'}`}>
      <input type="radio" name="allocation-mode" checked={checked} disabled={disabled}
             onChange={onChange} className="mt-0.5 h-4 w-4" />
      <span>
        <span className="block text-[13px] font-medium text-ink">{title}</span>
        <span className="block text-xs text-muted">{detail}</span>
      </span>
    </label>
  );
}
